import type { BaseProps } from '../interfaces';
import type { RequiringAction as IRequiringAction } from '@paid-ui/types';

import { forwardRef, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { groupBy } from 'lodash';
import { PackageOpenIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { twMerge } from 'tailwind-merge';

import { SearchInput } from '@paid-ui/components/search-input';
import { utc2Mel } from '@paid-ui/utils/datetime';
import {
  formatRequiringActionDescription,
  formatRequiringActionTitle,
} from '@paid-ui/utils/requiring-action';

import { RequiringAction, RequiringActionSkeleton } from '../requiring-action';
import { Separator } from '../separator';
import { Space } from '../space';

const sortByDate = (date1: string, date2: string) => {
  return dayjs(date2).valueOf() - dayjs(date1).valueOf();
};

const sortRequiringActions = (ra1: IRequiringAction, ra2: IRequiringAction) => {
  return dayjs(ra2.createdTimestamp).valueOf() - dayjs(ra1.createdTimestamp).valueOf();
};

const formatDateRelative = (date: string) => {
  const d = dayjs(date);

  if (d.isToday()) {
    return 'Today';
  }
  if (d.isYesterday()) {
    return 'Yesterday';
  }
  return d.format('DD MMM YYYY');
};

export interface RequiringActionsProps extends BaseProps {
  data: IRequiringAction[];
  loading?: boolean;
  disabled?: boolean;
  groupByDate?: boolean;
  emptyText?: string;
  onItemClick?: (data: IRequiringAction) => void;
  onItemDismiss?: (data: IRequiringAction) => void;
}

export const RequiringActions = forwardRef<HTMLDivElement, RequiringActionsProps>((props, ref) => {
  const {
    data,
    loading,
    disabled,
    groupByDate,
    emptyText = 'No required actions',
    onItemClick,
    onItemDismiss,
    ...restProps
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchSubmit = () => {
    setSearchTerm(inputValue.trim());
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (newValue === '' && inputValue !== '') {
      setSearchTerm('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const filteredData = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return data.filter((ra) => {
      const title = formatRequiringActionTitle(ra).toLowerCase();
      const description = formatRequiringActionDescription(ra).toLowerCase();
      return title.includes(lowerSearchTerm) || description.includes(lowerSearchTerm);
    });
  }, [data, searchTerm]);

  if (loading) {
    return (
      <Space ref={ref} direction="vertical" inline={false} {...restProps}>
        {Array.from({ length: 2 }).map((_, index) => (
          <RequiringActionSkeleton key={index} />
        ))}
      </Space>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-y-4 px-4 py-16 text-center">
        <PackageOpenIcon className="h-8 w-8 text-grey-tertiary" />
        <div className="text-sm text-grey-dark">No required actions</div>
      </div>
    );
  }

  if (groupByDate) {
    const sortedRequiringActionsGroupedByDate = Object.entries(
      groupBy(filteredData, (ra) => utc2Mel(ra.createdTimestamp).format('YYYY-MM-DD')),
    ).toSorted((a, b) => sortByDate(a[0], b[0]));

    return (
      <div {...restProps}>
        <div className="px-4 py-2.5">
          <SearchInput
            name="searchTerm"
            value={inputValue}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleSearchSubmit}
            onKeyDown={handleKeyDown}
            placeholder="Search"
            compact
          />
        </div>
        {filteredData.length > 0 ? (
          sortedRequiringActionsGroupedByDate.map(([date, ras]) => (
            <div key={date}>
              <div className="border-t border-grey-mid bg-grey-lightest px-4 py-2.5 text-xs text-grey-dark">
                {formatDateRelative(date)}
              </div>
              <div>
                <AnimatePresence mode="popLayout">
                  {ras.toSorted(sortRequiringActions).map((ra) => (
                    <motion.div
                      key={ra.id}
                      className="w-full"
                      initial={{ opacity: 1, height: 'auto' }}
                      exit={{
                        opacity: 0,
                        height: 0,
                        transition: {
                          opacity: { duration: 0.2 },
                          height: { duration: 0.3, ease: 'easeInOut' },
                        },
                      }}
                    >
                      <Separator decorative gap={0} />
                      <RequiringAction
                        data={ra}
                        disabled={disabled}
                        q={searchTerm}
                        onClick={onItemClick}
                        onDismiss={onItemDismiss}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center gap-y-4 px-4 py-16 text-center">
            <PackageOpenIcon className="h-8 w-8 text-grey-tertiary" />
            <div className="text-sm text-grey-dark">No required actions</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div {...restProps}>
      <div className="px-4 py-2.5">
        <SearchInput
          name="searchTerm"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleSearchSubmit}
          onKeyDown={handleKeyDown}
          placeholder="Search"
          compact
        />
      </div>
      <AnimatePresence mode="popLayout">
        {data.map((item) => (
          <motion.div
            key={item.id}
            className="w-full"
            initial={{ opacity: 1, height: 'auto' }}
            exit={{
              opacity: 0,
              height: 0,
              transition: {
                opacity: { duration: 0.2 },
                height: { duration: 0.3, ease: 'easeInOut' },
              },
            }}
          >
            <Separator decorative gap={0} />
            <RequiringAction
              data={item}
              disabled={disabled}
              q={searchTerm}
              onClick={onItemClick}
              onDismiss={onItemClick}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
});

RequiringActions.displayName = 'RequiringActions';
